// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Base stylesheet applied to the `XngCarousel` root wrapper.
 *
 * Exposes the carousel layout primitives, the controls row
 * (`.xng-controls-container`), the pagination dots (`.carousel-pagination`),
 * and the custom scrollbar (`.carousel-scrollbar`).
 *
 * Pulls colors and breakpoints from the XDL theme.
 */

import { IconWrapper } from "@insite/mobius/Icon";
import { getProp } from "@insite/mobius/utilities";
import { color, xScreen } from "@vanmeter/XDL/Utilities";
import { css } from "styled-components";

// ─── Design tokens ────────────────────────────────────────────────────────────

const SCROLLBAR_SIZE = "4px";
const SCROLLBAR_THUMB_WIDTH = "50px";
const SCROLLBAR_RADIUS = "2px";
const PAGINATION_DOT_SIZE = "10px";

// ─── Style partials ───────────────────────────────────────────────────────────

export const navButtonCss = (side: "right" | "left", smallNavIcon?: boolean, inline?: boolean) =>
    inline
        ? {
              css: css`
                margin-${side === "left" ? "right" : "left"}: 12px;;
                  ${IconWrapper} {
                      position: static;
                      color: ${color("common.white")};
                  }
                  &:hover {
                      ${IconWrapper} {
                          color: ${color("common.ui17")};
                      }
                  }
              `,
          }
        : {
              css: css`
                  position: absolute;
                  top: 50%;
                  transform: translateY(-50%);
                  transition: all ${getProp("theme.transition.duration.regular", "300")}ms ease-in-out;

                  ${smallNavIcon &&
                  css`
                      top: 6px;
                      transform: none;
                  `}
                  background: transparent;

                  ${side === "right" &&
                  css`
                      right: 2px;
                  `}

                  ${side === "left" &&
                  css`
                      left: 2px;
                  `}
                  box-shadow: 0 0 4px 0 ${color("common.black", 0.5)};

                  &&& > ${IconWrapper} {
                      color: ${color("secondary")};
                      transition: all ${getProp("theme.transition.duration.regular", "300")}ms ease-in-out;

                      path {
                          fill: ${color("primary")};
                      }

                      &:hover {
                          color: ${color("common.ui17")};
                      }
                      &:focus {
                          color: ${color("common.ui8")};
                      }

                      &,
                      svg {
                          width: 56px;
                          height: 56px;

                          ${smallNavIcon &&
                          css`
                              width: 40px;
                              height: 40px;
                          `}
                          ${xScreen("lte", "md")} {
                              width: 40px;
                              height: 40px;
                          }
                      }
                  }
                  z-index: 10;
                  padding: 0;
                  border-radius: 50%;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  border: 0;
                  outline: none !important;

                  &:disabled {
                      visibility: hidden;
                  }

                  ${xScreen("lte", "md")} {
                      display: none;
                  }
              `,
          };

const scrollbarThumb = css`
    position: absolute;
    border-radius: ${SCROLLBAR_RADIUS};
    transition: background-color 0.2s ease;
    cursor: grab;

    &:hover {
        background: ${color("common.ui8")};
    }

    &:active {
        cursor: grabbing;
    }
`;

const paginationDot = css`
    width: ${PAGINATION_DOT_SIZE};
    height: ${PAGINATION_DOT_SIZE};
    min-height: unset;
    padding: 0;
    margin: 0 !important;
    border: 1px solid ${color("common.ui4")};
    border-radius: 100%;
    background: transparent;
    opacity: 1;
    cursor: pointer;

    &:hover:not(.is-active) {
        background: ${color("common.ui3")};
        border-color: ${color("common.ui3")};
        outline: none;
    }

    &.is-active {
        background: ${color("common.ui5")};
        border-color: ${color("common.ui5")};
    }
`;

// ─── Main stylesheet ──────────────────────────────────────────────────────────

/**
 * Root CSS for the carousel. Composed into the `container` slot of the
 * default `carouselStyles` from `XngCarousel`.
 */
export const xCarouselStyle = css`
    width: 100%;
    display: flex;
    flex-direction: column;

    /* ── Controls row ── */

    .xng-controls-container {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: 12px;
        gap: 16px;

        > .xng-controls-container-pagination {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
            gap: 16px;
            width: 100%;
        }

        & > div,
        & > button {
            margin: 0;
        }

        /* Scrollbar override when rendered inside the controls row */
        > .xng-controls-container-pagination > .carousel-scrollbar.carousel-scrollbar-horizontal {
            height: ${SCROLLBAR_SIZE};
            width: 100%;
            position: relative;
            inset: unset;
            transform: initial;

            .carousel-scrollbar-thumb {
                background: ${color("common.ui9")};
                width: ${SCROLLBAR_THUMB_WIDTH};
                opacity: 0.5;
            }
        }

        &.inline {
            display: flex;
            align-items: center;
            position: absolute;
            gap: 16px;
            bottom: 24px;
            left: 24px;
            margin-top: 16px;
            z-index: 1;

            ${xScreen("lte", "md")} {
                left: 16px;
                bottom: 16px;
            }
        }

        /* XNG-Changes: Below-carousel arrows only on mobile/tablet; desktop uses side arrows */
        &:not(.inline) {
            .arrow-on-mobile {
                display: none;

                ${xScreen("lte", "md")} {
                    display: flex;
                    position: relative;
                    top: unset;
                    left: unset;
                    transform: none;
                }
            }

            ${xScreen("lte", "md")} {
                > .xng-controls-container-pagination {
                    width: 100%;
                    flex-direction: column;
                    gap: 12px;
                }
            }
        }
    }

    /* ── Pagination ── */

    .carousel-pagination {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-wrap: wrap;
        gap: 8px 4px;
        text-align: center;

        &.carousel-pagination-lock {
            display: none;
        }
        .pause-play-btn {
            margin-left: 8px;
            &:hover ${IconWrapper} {
                color: ${color("common.ui17")};
            }

            &:focus:not(:hover) ${IconWrapper} {
                color: ${color("common.white")};
            }
        }
    }

    /* ── Scrollbar ── */

    .carousel-scrollbar {
        background: ${color("common.ui2")};
        border-radius: ${SCROLLBAR_RADIUS};

        &.carousel-scrollbar-lock {
            display: none;
        }

        .carousel-scrollbar-thumb {
            ${scrollbarThumb}
        }
    }

    .carousel-scrollbar-horizontal {
        .carousel-scrollbar-thumb {
            height: 100%;
        }
    }

    .carousel-scrollbar-vertical {
        width: ${SCROLLBAR_SIZE};

        .carousel-scrollbar-thumb {
            width: 100%;
        }
    }
`;
